import React from 'react';

const fmtR = (n) => {
  if (n == null) return '—';
  if (Math.abs(n) >= 1e9) return `R ${(n / 1e9).toFixed(2)}bn`;
  if (Math.abs(n) >= 1e6) return `R ${(n / 1e6).toFixed(1)}m`;
  if (Math.abs(n) >= 1e3) return `R ${Math.round(n / 1e3)}k`;
  return `R ${Math.round(n)}`;
};
const fmtSqm = (v) => (v != null ? `${Math.round(v).toLocaleString()} m²` : '—');

const CENTRE_TYPE_LABELS = {
  super_regional: 'Super-regional centre',
  regional: 'Regional centre',
  small_regional: 'Small-regional centre',
  community: 'Community centre',
  neighbourhood: 'Neighbourhood centre',
};

const chipClass = (color) =>
  color === 'green' ? 'green' : color === 'amber' ? 'amber' : 'red';

export default function FeasibilityInsightPanel({
  result, loading, error, hasPin, radiusKm,
  credits, isAdmin,
  onUnlock, unlocking, onBuyCredits,
  onRun, onViewFull,
}) {
  // ---------- prompt / empty ----------
  if (!loading && !result && !error) {
    return (
      <div style={styles.center}>
        <div style={styles.kicker}>SHOPPING CENTRE FEASIBILITY</div>
        <div style={styles.title}>
          {!hasPin ? 'Drop a pin on the map' : 'Ready to analyse'}
        </div>
        <p style={styles.text}>
          Total GLA demand by tenant category, competing centres, anchor
          recommendation and investment viability — across all nine retail
          categories in a {radiusKm || 5} km catchment.
        </p>
        <button className="sq-btn primary" style={{ marginTop: 16, width: '100%' }} onClick={onRun} disabled={!hasPin}>
          {!hasPin ? 'Drop a pin first' : 'Run feasibility preview'}
        </button>
      </div>
    );
  }

  if (error && !loading && !result) {
    return (
      <div style={styles.center}>
        <div style={{ ...styles.title, color: 'var(--s-red)' }}>No study for this pin</div>
        <p style={styles.text}>{error}</p>
      </div>
    );
  }

  if (loading && !result) {
    return (
      <div style={{ padding: 22 }}>
        <div className="sq-skel" style={{ width: 150, height: 46, marginBottom: 14 }} />
        <div className="sq-skel" style={{ width: '55%', height: 16, marginBottom: 26 }} />
        {[...Array(6)].map((_, i) => (
          <div key={i} className="sq-skel" style={{ height: 12, marginBottom: 12, width: `${90 - i * 6}%` }} />
        ))}
      </div>
    );
  }

  const r = result;
  const locked = !!r.locked;
  const f = r.feasibility || {};
  const centres = r.competing_centres || f.competing_centres || [];
  const centreType = locked ? r.centre_type : f.centre_type;
  const tenantMix = (f.tenant_mix || []).filter(t => t.supportable_gla_sqm > 0);
  const maxGla = tenantMix.length ? Math.max(...tenantMix.map(t => t.supportable_gla_sqm), 1) : 1;

  return (
    <div style={{ ...styles.scroll, opacity: loading ? 0.55 : 1 }}>

      {/* ---------- verdict hero ---------- */}
      <div style={styles.hero}>
        <div>
          <div style={styles.bigGla}>
            {locked
              ? `${(r.gla_band.low / 1000).toFixed(0)}–${(r.gla_band.high / 1000).toFixed(0)}k`
              : fmtSqm(f.recommended_gla_sqm).replace(' m²', '')}
          </div>
          <div style={styles.glaSub}>SUPPORTABLE GLA · m²</div>
        </div>
        <div style={{ paddingTop: 4 }}>
          <span className={`sq-chip ${chipClass(r.verdict_color)}`}><i />{r.verdict}</span>
          <div style={styles.conf}>
            <b style={{ color: 'var(--s-text)' }}>{CENTRE_TYPE_LABELS[centreType] || centreType || '—'}</b>
            <br />{r.dominant_lsm} · {radiusKm || 5} km catchment
          </div>
        </div>
      </div>

      {/* ---------- demand summary ---------- */}
      <h3 className="sq-sec">Demand balance</h3>
      {locked ? (
        <div style={styles.lockCard}>
          <div style={styles.lockBlur}>
            <div style={styles.statK}>Unmet demand → GLA</div>
            <div style={styles.statV}>•,••• m² across 6 categories</div>
            <div style={{ ...styles.statK, marginTop: 12 }}>Recommended anchor</div>
            <div style={styles.statV}>•••••••••</div>
          </div>
          <div style={styles.lockInner}>
            <div style={styles.lockTitle}>Unlock the full feasibility study</div>
            <div style={styles.lockText}>
              Exact supportable GLA, the tenant mix broken down by category,
              per-category unmet demand, and the anchor recommendation —
              unlocked for this site for 30 days.
            </div>
            {credits > 0 || isAdmin ? (
              <button className="sq-btn primary" onClick={onUnlock} disabled={unlocking} style={{ width: '100%' }}>
                {unlocking ? 'Unlocking…' : isAdmin ? 'Unlock study (admin)' : `Unlock — 1 SC credit (${credits} left)`}
              </button>
            ) : (
              <button className="sq-btn primary" onClick={onBuyCredits} style={{ width: '100%' }}>
                Buy feasibility credits
              </button>
            )}
          </div>
        </div>
      ) : (
        <div style={styles.statRow}>
          <div style={styles.stat}>
            <div style={styles.statK}>Catchment spend</div>
            <div style={styles.statV}>{fmtR(f.total_catchment_spend)}<small style={styles.sm}> /mo</small></div>
          </div>
          <div style={{ ...styles.stat, background: 'rgba(255,106,61,0.08)', borderColor: 'rgba(255,106,61,0.25)' }}>
            <div style={styles.statK}>Unmet demand</div>
            <div style={styles.statV}>{fmtR(f.total_unmet_demand)}<small style={styles.sm}> /mo</small></div>
          </div>
        </div>
      )}

      {/* ---------- tenant mix ---------- */}
      {!locked && tenantMix.length > 0 && (
        <>
          <h3 className="sq-sec">Tenant mix · GLA by category</h3>
          {tenantMix.map((t) => (
            <div key={t.category} style={styles.tenantRow}>
              <span style={styles.tenantName}>{t.category}</span>
              <span style={styles.tenantBar}>
                <span style={{ display: 'block', height: '100%', borderRadius: 99, background: '#e8e4da', width: `${(t.supportable_gla_sqm / maxGla) * 100}%` }} />
              </span>
              <span style={styles.tenantGla}>{Math.round(t.supportable_gla_sqm).toLocaleString()}</span>
            </div>
          ))}
        </>
      )}

      {/* ---------- competing centres ---------- */}
      <h3 className="sq-sec">
        Competing centres · {locked ? r.competitor_count : centres.length} in catchment
      </h3>
      {centres.length === 0 ? (
        <div style={{ fontSize: 12.5, color: 'var(--s-faint)' }}>
          No competing shopping centres in the catchment — clear whitespace.
        </div>
      ) : (
        centres.slice(0, 6).map((c, i) => (
          <div key={`${c.name}-${i}`} style={styles.rowItem}>
            <span style={styles.swd} />
            <span style={styles.rowName}>
              {c.name}
              <small style={styles.rowSub}>
                {fmtSqm(c.gla_sqm)} · {CENTRE_TYPE_LABELS[c.centre_type]?.replace(' centre', '') || c.centre_type}
              </small>
            </span>
            <span style={styles.rowVal}>{c.dist_km} km</span>
          </div>
        ))
      )}

      {/* ---------- anchor ---------- */}
      {!locked && f.anchor_recommendation && (
        <>
          <h3 className="sq-sec">Anchor recommendation</h3>
          <div style={styles.anchor}>{f.anchor_recommendation}</div>
        </>
      )}

      <div style={styles.prov}>
        Census subplace demographics · SACSC trading densities · shopping-centre POI register
        {locked ? ' · free preview' : ''}
      </div>

      {!locked && (
        <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
          <button className="sq-btn" style={{ flex: 1 }} onClick={onViewFull}>
            Open full study
          </button>
        </div>
      )}
    </div>
  );
}

const styles = {
  scroll: { padding: '22px 22px 18px', transition: 'opacity 0.2s' },
  center: {
    height: '100%', display: 'flex', flexDirection: 'column',
    alignItems: 'flex-start', justifyContent: 'center', padding: '0 26px',
  },
  kicker: { font: '600 10px var(--s-sans)', letterSpacing: '0.2em', color: 'var(--s-accent)', marginBottom: 10 },
  title: { fontFamily: 'var(--s-serif)', fontSize: 24, fontWeight: 700, color: 'var(--s-text)', lineHeight: 1.2, marginBottom: 10 },
  text: { fontSize: 13, color: 'var(--s-muted)', lineHeight: 1.7 },

  hero: { display: 'flex', gap: 16, alignItems: 'flex-start' },
  bigGla: { font: '700 42px/1 var(--s-mono)', letterSpacing: '-0.04em', color: 'var(--s-text)', whiteSpace: 'nowrap' },
  glaSub: { font: '500 10px var(--s-mono)', color: 'var(--s-faint)', marginTop: 6, letterSpacing: '0.1em' },
  conf: { fontSize: 11.5, color: 'var(--s-muted)', marginTop: 8, lineHeight: 1.6 },

  statRow: { display: 'flex', gap: 10 },
  stat: { flex: 1, border: '1px solid var(--s-line2)', borderRadius: 11, padding: '11px 13px', background: 'var(--s-w025)' },
  statK: { fontSize: 10.5, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--s-faint)', marginBottom: 5 },
  statV: { font: '600 16px var(--s-mono)', letterSpacing: '-0.02em', color: 'var(--s-text)' },
  sm: { fontSize: 11, color: 'var(--s-muted)', fontWeight: 500 },

  lockCard: { position: 'relative', border: '1px solid var(--s-line)', borderRadius: 12, overflow: 'hidden' },
  lockBlur: { padding: '14px 16px', filter: 'blur(7px)', userSelect: 'none', pointerEvents: 'none', opacity: 0.8 },
  lockInner: { borderTop: '1px solid var(--s-line)', padding: '14px 16px 16px', background: 'rgba(255,106,61,0.05)' },
  lockTitle: { fontWeight: 700, fontSize: 14, color: 'var(--s-text)', marginBottom: 6 },
  lockText: { fontSize: 12.5, color: 'var(--s-muted)', lineHeight: 1.6, marginBottom: 12 },

  tenantRow: { display: 'grid', gridTemplateColumns: '120px 1fr 52px', alignItems: 'center', gap: 10, padding: '5px 0' },
  tenantName: { fontSize: 12.5, color: 'var(--s-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  tenantBar: { height: 6, borderRadius: 99, background: 'var(--s-w07)', overflow: 'hidden' },
  tenantGla: { font: '600 12px var(--s-mono)', textAlign: 'right', color: 'var(--s-text)' },

  rowItem: { display: 'flex', alignItems: 'center', gap: 11, padding: '8px 0', borderBottom: '1px solid var(--s-line2)' },
  swd: { width: 8, height: 8, borderRadius: 2, flexShrink: 0, background: 'var(--s-blue)' },
  rowName: { flex: 1, fontSize: 13, color: 'var(--s-text)', minWidth: 0 },
  rowSub: { display: 'block', color: 'var(--s-faint)', fontSize: 11, marginTop: 1 },
  rowVal: { font: '600 12.5px var(--s-mono)', color: 'var(--s-muted)', whiteSpace: 'nowrap' },

  anchor: {
    fontSize: 13, color: '#d8d4ca', lineHeight: 1.6,
    borderLeft: '3px solid var(--s-accent)', paddingLeft: 12,
    fontFamily: 'var(--s-serif)',
  },
  prov: {
    marginTop: 22, paddingTop: 14, borderTop: '1px solid var(--s-line2)',
    font: '400 10.5px var(--s-mono)', color: 'var(--s-faint)', lineHeight: 1.7,
  },
};
